import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../ui/alert-dialog';
import { CheckCircle2, XCircle, AlertTriangle, Eye, Copy, ShieldCheck, Download, FileCode2, Loader2 } from 'lucide-react';
import { CATEGORY_LABELS } from '../../../lib/canFrameDecoder';

const downloadText = (text, filename) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const IssueList = ({ issues, onJumpToFrame, testId, frameLabel, categoryLabels }) => (
  <ul className="space-y-2" data-testid={testId}>
    {issues.map((i, k) => (
      <li
        key={k}
        className={`flex items-start gap-2 rounded-md border p-2 text-sm ${i.severity === 'error'
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-amber-400/60 bg-amber-50 dark:bg-amber-950/30'}`}
      >
        {i.severity === 'error'
          ? <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
          : <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />}
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {i.frames.length > 0 ? i.frames.map((f) => (
              <button
                key={f} type="button" onClick={() => onJumpToFrame(f)}
                className="rounded bg-foreground/90 px-1.5 py-0.5 font-mono text-[11px] font-bold text-background hover:bg-autoxium-blue"
              >
                {frameLabel(f)}
              </button>
            )) : (
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] font-bold">Whole file</span>
            )}
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              {categoryLabels[i.category] || i.category}{i.line ? ` · line ${i.line}` : ''}
            </span>
          </div>
          <p className="break-words">{i.message}</p>
        </div>
      </li>
    ))}
  </ul>
);

const DbcCode = ({ text, issues, prefix }) => {
  const lineSeverity = {};
  issues.forEach((i) => {
    if (i.line && lineSeverity[i.line] !== 'error') lineSeverity[i.line] = i.severity;
  });
  const lines = text.split('\n');
  const firstFlagged = Object.keys(lineSeverity).map(Number).sort((a, b) => a - b)[0];
  const preRef = useRef(null);
  // Bring the first flagged line into view when the viewer opens, since issues usually
  // sit far below the NS_ header block.
  useEffect(() => {
    const pre = preRef.current;
    const row = firstFlagged && pre?.querySelector(`[data-line="${firstFlagged}"]`);
    if (row) pre.scrollTop = Math.max(0, row.offsetTop - pre.clientHeight / 3);
  }, [firstFlagged]);
  return (
    <pre ref={preRef} className="text-xs font-mono leading-5 bg-slate-950 text-slate-100 rounded-md p-3 overflow-auto max-h-[60vh]" data-testid={`${prefix}-dbc-code`}>
      {lines.map((line, k) => {
        const sev = lineSeverity[k + 1];
        const bg = sev === 'error' ? 'bg-red-900/60' : sev === 'warning' ? 'bg-amber-900/50' : '';
        return (
          <div key={k} data-line={k + 1} className={`flex ${bg}`}>
            <span className="select-none w-10 shrink-0 pr-3 text-right text-slate-500">{k + 1}</span>
            <span className="whitespace-pre">{line || ' '}</span>
          </div>
        );
      })}
    </pre>
  );
};

// Shared by the CAN Raw Frame Decoder (the defaults) and the J1939 Frame Decoder, which
// passes its own test-id prefix, frame label and category labels.
const defaultFrameLabel = (f) => `Raw Frame ${f + 1}`;

const DbcPanel = ({
  dbc, validation, loading, onValidate, onJumpToFrame,
  prefix = 'cfd', frameLabel = defaultFrameLabel, categoryLabels = CATEGORY_LABELS, sourceLabel = 'raw frames',
}) => {
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!dbc || !validation) {
    return (
      <Card className="technical-card"><CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">Generating DBC…</p>
      </CardContent></Card>
    );
  }

  const { passed, errorCount, warningCount, issues, checks } = validation;
  const issueCount = errorCount;
  const invalidFilename = dbc.filename.replace(/\.dbc$/, '_INVALID.dbc');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dbc.text);
      toast.success('DBC copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const handleDownload = () => {
    if (passed) {
      downloadText(dbc.text, dbc.filename);
      toast.success(`Downloaded ${dbc.filename}.`);
    } else {
      setAcknowledged(false);
      setConfirmOpen(true);
    }
  };

  const confirmInvalidDownload = () => {
    downloadText(dbc.text, invalidFilename);
    toast.warning(`Downloaded ${invalidFilename} with ${issueCount} validation error${issueCount === 1 ? '' : 's'}.`);
  };

  return (
    <Card className="technical-card scroll-mt-24" id={`${prefix}-dbc-panel`} data-testid={`${prefix}-dbc-panel`}>
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
          <FileCode2 className="h-5 w-5 text-autoxium-blue" /> Generated DBC
        </CardTitle>
        <CardDescription>
          {dbc.messageCount} message{dbc.messageCount === 1 ? '' : 's'} (BO_) and {dbc.signalCount} signal{dbc.signalCount === 1 ? '' : 's'} (SG_)
          from all {sourceLabel}, validated automatically after every change.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {passed ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-500/60 bg-emerald-50 dark:bg-emerald-950/30 p-4" data-testid={`${prefix}-validation-banner`}>
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            <span className="font-chivo font-bold uppercase tracking-tight text-emerald-700 dark:text-emerald-400">DBC Validation Passed</span>
            {warningCount > 0 && (
              <span className="text-sm text-amber-700 dark:text-amber-400">· {warningCount} warning{warningCount === 1 ? '' : 's'}</span>
            )}
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-auto" />}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/60 bg-destructive/5 p-4" data-testid={`${prefix}-validation-banner`}>
            <XCircle className="h-6 w-6 text-destructive" />
            <div>
              <div className="font-chivo font-bold uppercase tracking-tight text-destructive">DBC Validation Failed</div>
              <div className="text-sm text-destructive" data-testid={`${prefix}-issue-count`}>
                {dbc.messageCount === 0 && issueCount === 0
                  ? 'No valid frames to put in the DBC.'
                  : `${issueCount} Issue${issueCount === 1 ? '' : 's'} Found`}
                {warningCount > 0 && <span className="text-amber-700 dark:text-amber-400"> · {warningCount} warning{warningCount === 1 ? '' : 's'}</span>}
              </div>
            </div>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-auto" />}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid={`${prefix}-checks`}>
          {checks.map((c) => (
            <div key={c.key} className="flex items-center gap-2 rounded-md border border-structure bg-muted/30 px-3 py-2 text-sm">
              {c.status === 'pass' && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}
              {c.status === 'warn' && <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />}
              {c.status === 'fail' && <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
              <span className="min-w-0">{c.label}</span>
              {c.count > 0 && <span className="ml-auto font-mono text-xs text-muted-foreground">{c.count}</span>}
            </div>
          ))}
        </div>

        {issues.length > 0 && <IssueList issues={issues} onJumpToFrame={onJumpToFrame} testId={`${prefix}-issues`} frameLabel={frameLabel} categoryLabels={categoryLabels} />}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" variant="outline" onClick={() => setViewOpen(true)} data-testid={`${prefix}-view-btn`}>
            <Eye className="h-4 w-4" /> View DBC
          </Button>
          <Button type="button" variant="outline" onClick={handleCopy} data-testid={`${prefix}-copy-btn`}>
            <Copy className="h-4 w-4" /> Copy DBC
          </Button>
          <Button type="button" variant="outline" onClick={onValidate} disabled={loading} data-testid={`${prefix}-validate-btn`}>
            <ShieldCheck className="h-4 w-4" /> Validate DBC
          </Button>
          {passed ? (
            <Button type="button" onClick={handleDownload} className="bg-autoxium-blue hover:bg-autoxium-blue/90" data-testid={`${prefix}-download-btn`}>
              <Download className="h-4 w-4" /> Download .DBC
            </Button>
          ) : (
            <Button type="button" variant="destructive" onClick={handleDownload} disabled={dbc.messageCount === 0} data-testid={`${prefix}-download-invalid-btn`}>
              <Download className="h-4 w-4" /> Download Invalid DBC
            </Button>
          )}
        </div>
      </CardContent>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className={`${passed ? 'max-w-4xl' : 'max-w-6xl'} w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto`} data-testid={`${prefix}-view-dialog`}>
          <DialogHeader className="min-w-0">
            <DialogTitle className="font-chivo uppercase tracking-tight flex flex-wrap items-center gap-x-2 gap-y-1 pr-6 break-all">
              {dbc.filename}
              {passed
                ? <span className="text-xs font-sans normal-case text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Validation passed</span>
                : <span className="text-xs font-sans normal-case text-destructive flex items-center gap-1"><XCircle className="h-4 w-4" /> {issueCount} issue{issueCount === 1 ? '' : 's'}</span>}
            </DialogTitle>
            <DialogDescription>
              {passed ? 'The generated CAN database, ready to download.' : 'Highlighted lines have validation issues; the full list is alongside the file.'}
            </DialogDescription>
          </DialogHeader>
          <div className={passed ? 'min-w-0' : 'grid gap-4 lg:grid-cols-5 min-w-0'}>
            <div className={passed ? 'min-w-0' : 'lg:col-span-3 min-w-0'}><DbcCode text={dbc.text} issues={issues} prefix={prefix} /></div>
            {!passed && (
              <div className="lg:col-span-2 min-w-0 lg:max-h-[60vh] lg:overflow-y-auto">
                <IssueList
                  issues={issues}
                  onJumpToFrame={(f) => { setViewOpen(false); onJumpToFrame(f); }}
                  testId={`${prefix}-view-issues`}
                  frameLabel={frameLabel}
                  categoryLabels={categoryLabels}
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={handleCopy}><Copy className="h-4 w-4" /> Copy DBC</Button>
            {passed ? (
              <Button type="button" onClick={handleDownload} className="bg-autoxium-blue hover:bg-autoxium-blue/90"><Download className="h-4 w-4" /> Download .DBC</Button>
            ) : (
              <Button type="button" variant="destructive" onClick={() => { setViewOpen(false); handleDownload(); }}><Download className="h-4 w-4" /> Download Invalid DBC</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`${prefix}-invalid-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Download an invalid DBC?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This file failed validation with {issueCount} error{issueCount === 1 ? '' : 's'}. CAN tools may reject it,
              or worse, load it and decode signals wrongly. It will be saved as <span className="font-mono">{invalidFilename}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2">
            <Checkbox id={`${prefix}-ack`} checked={acknowledged} onCheckedChange={(v) => setAcknowledged(v === true)} data-testid={`${prefix}-invalid-ack`} />
            <Label htmlFor={`${prefix}-ack`} className="text-sm leading-snug">
              I understand this DBC has validation errors and should not be used as-is.
            </Label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!acknowledged}
              onClick={confirmInvalidDownload}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid={`${prefix}-invalid-confirm-btn`}
            >
              Download Invalid DBC
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default DbcPanel;
